// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useMemo } from 'react';
import { PinIcon } from '@loa/ui-components';
import type { QuoteOption } from '../../types/quote';
import { humanizeMetadataKey } from './metadataLabel';

type QuoteOptionCardProps = Readonly<{
  option: QuoteOption;
  selecting: boolean;
  onSelect: () => void;
  /**
   * Optional handler for the "pin / save for later" button in the card
   * header. When omitted, the button still renders but does nothing on
   * click — matches the visual-only behaviour pinned in the original plan.
   */
  onPin?: () => void;
  pinned?: boolean;
}>;

const formatPercent = (value: number, decimals = 1): string => {
  if (Number.isInteger(value)) return `${value}%`;
  return `${value.toFixed(decimals)}%`;
};

const formatSchedule = (values: readonly number[]): string => {
  if (values.length === 0) return 'NIL';
  return values.map((v) => formatPercent(v)).join(', ');
};

const formatClawbacks = (values: readonly number[]): string => {
  if (values.length === 0) return '—';
  return values.map((v) => `${Math.round(v)}`).join('/') + '%';
};

const num = (snapshot: Record<string, unknown> | undefined, key: string): number | undefined => {
  const v = snapshot?.[key];
  return typeof v === 'number' ? v : undefined;
};

/** Commission shown as a fixed value, a min–max range, or blank. */
const formatCommission = (snapshot?: Record<string, unknown>): string | null => {
  const fixed = num(snapshot, 'initialCommissionFixed');
  if (fixed != null) return `${formatPercent(fixed)} fixed`;
  const min = num(snapshot, 'initialCommissionMin');
  const max = num(snapshot, 'initialCommissionMax');
  if (min != null && max != null) return `${formatPercent(min)}–${formatPercent(max)}`;
  if (max != null) return `up to ${formatPercent(max)}`;
  return null;
};

const formatMoney = (v?: number): string | null =>
  v == null ? null : `€${v.toLocaleString('en-IE')}`;

/** Provider-specific key-value extras captured on the rate card, if any. */
const metadataRows = (snapshot?: Record<string, unknown>): Array<{ label: string; value: string }> => {
  const meta = snapshot?.metadata;
  if (!meta || typeof meta !== 'object') return [];
  return Object.entries(meta as Record<string, unknown>)
    .filter(([, v]) => v != null && String(v) !== '')
    .map(([label, v]) => ({ label: humanizeMetadataKey(label), value: String(v) }));
};

const formatContributionRange = (snapshot?: Record<string, unknown>): string | null => {
  const min = formatMoney(num(snapshot, 'minContribution'));
  const max = formatMoney(num(snapshot, 'maxContribution'));
  if (min && max) return `${min} – ${max}`;
  if (min) return `from ${min}`;
  if (max) return `up to ${max}`;
  return null;
};

const initials = (name: string): string => {
  if (!name) return '??';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

/**
 * One card on the Quote Options screen — exactly the layout from the design:
 * provider header, then five labelled rows, then the Select Quote checkbox.
 *
 * The card is presentational; selection is driven by the parent page so only
 * one card is ever marked selected at a time.
 */
export function QuoteOptionCard({
  option,
  selecting,
  onSelect,
  onPin,
  pinned = false,
}: QuoteOptionCardProps) {
  const allocationLabel = useMemo(
    () => `${Math.round(option.allocationPercent)}%`,
    [option.allocationPercent],
  );
  const amcLabel = useMemo(() => formatPercent(option.amcPercent, 2), [option.amcPercent]);
  const fmcLabel = useMemo(
    () => formatSchedule(option.fundManagementCharges),
    [option.fundManagementCharges],
  );
  const exitLabel = useMemo(
    () => formatSchedule(option.exitPenalties),
    [option.exitPenalties],
  );
  const clawbackLabel = useMemo(
    () => formatClawbacks(option.clawbacks),
    [option.clawbacks],
  );
  const commissionLabel = useMemo(() => formatCommission(option.termsSnapshot), [option.termsSnapshot]);
  const contributionRange = useMemo(
    () => formatContributionRange(option.termsSnapshot),
    [option.termsSnapshot],
  );
  const extraRows = useMemo(() => metadataRows(option.termsSnapshot), [option.termsSnapshot]);

  return (
    <div
      className="quote-option-card"
      data-testid={`quote-option-${option.id}`}
      data-selected={option.selected}
    >
      <div className="quote-option-card__header">
        <div className="quote-option-card__provider">
          {option.providerLogoUrl ? (
            <img
              src={option.providerLogoUrl}
              alt={option.providerName}
              className="quote-option-card__logo"
            />
          ) : (
            <span className="quote-option-card__logo-fallback" aria-hidden>
              {initials(option.providerName)}
            </span>
          )}
          <span className="quote-option-card__name">{option.providerName}</span>
          {option.brokerageSpecific && (
            <span
              className="border border-grey-06 rounded px-2 py-0.5 text-body-04 text-grey-02"
              data-testid={`quote-option-brokerage-badge-${option.id}`}
            >
              Your terms
            </span>
          )}
        </div>
        <button
          type="button"
          className="quote-option-card__pin-btn"
          onClick={onPin}
          aria-label={pinned ? 'Unpin quote option' : 'Pin quote option'}
          aria-pressed={pinned}
          data-testid={`quote-option-pin-${option.id}`}
        >
          <PinIcon pinned={pinned} aria-hidden />
        </button>
      </div>

      {option.qualifies === false ? (
        <p
          className="m-0 mb-2 text-body-04 text-grey-03"
          data-testid={`quote-option-nonqualifying-${option.id}`}
        >
          Outside this product's contribution range
        </p>
      ) : option.qualifies === true ? (
        <p
          className="m-0 mb-2 text-body-04 text-green-01"
          data-testid={`quote-option-qualifying-${option.id}`}
        >
          ✓ Matches the contribution
        </p>
      ) : null}

      <dl className="quote-option-card__rows">
        <Row label="Allocation" value={allocationLabel} />
        <Row label="AMC" value={amcLabel} />
        <Row label="Fund Management Charge / Trail Options" value={fmcLabel} />
        {commissionLabel && <Row label="Initial commission" value={commissionLabel} />}
        <Row label="Exit penalties" value={exitLabel} />
        <Row label="Clawback" value={clawbackLabel} />
        {contributionRange && <Row label="Contribution range" value={contributionRange} />}
        {extraRows.map((row) => <Row key={row.label} label={row.label} value={row.value} />)}
      </dl>

      <button
        type="button"
        className="quote-option-card__select-btn"
        onClick={onSelect}
        disabled={selecting}
        aria-pressed={option.selected}
      >
        <span
          className="quote-option-card__checkbox"
          data-checked={option.selected}
          aria-hidden
        />
        Select Quote
      </button>
    </div>
  );
}

function Row({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="quote-option-card__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
